import { useCallback } from 'react';

import { useQueryClient } from '@mint-lib/query-client';
import { ApprovalFlows } from '@mint-sdk/api-approval-flows';

type Payload = { flowId: string };

const useDeleteFlowMutation = () => {
  const { mutateAsync, isPending } = ApprovalFlows.deleteFlow();
  const queryClient = useQueryClient();

  const deleteFlow = useCallback(
    async ({ flowId }: Payload) => {
      const { queryKey } = ApprovalFlows.listFlows({
        data: {
          sort: 'asc',
          options: [],
        },
      });
      const prevQueryData =
        queryClient.getQueryData<ApprovalFlows.FlowListResponse>(queryKey);

      try {
        queryClient.setQueryData<ApprovalFlows.FlowListResponse>(
          queryKey,
          (oldData) => {
            if (!oldData?.data?.items) {
              return oldData;
            }

            return {
              ...oldData,
              data: {
                ...oldData.data,
                items: oldData.data.items.filter((flow) => flow.id !== flowId),
              },
            };
          },
        );

        await mutateAsync({
          urlParams: {
            flowId,
          },
        });
        ApprovalFlows.getFlow.invalidate(queryClient, {
          urlParams: {
            flowId,
          },
        });
        ApprovalFlows.listFlows.invalidateAll(queryClient, {});
      } catch {
        queryClient.setQueryData<ApprovalFlows.FlowListResponse>(
          queryKey,
          prevQueryData,
        );
        throw new Error('Flow deleting failed');
      }
    },
    [mutateAsync, queryClient],
  );

  return {
    deleteFlow,
    isPending,
  };
};

export default useDeleteFlowMutation;
